import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';

import { detailFooter, detailSocialMedia, getFooter, getSocialMedia } from 'redux-api/slices/footerSlice';
import { PATHS, externalRecourse, REQUEST_STATUSES, emailsFooter } from 'common/constants';

import Loader from 'components/Loader/Loader';
import { generateRel } from 'common/helpers';
import logo from '../../assets/icons/Logo.svg';
import classes from './Footer.module.scss';


const Footer = () => {


  const dispatch = useDispatch();
  const [ active, setActive ] = useState(null);
  const { t } = useTranslation();

  const linksFooter = [
    { text: t('AllCourses'), link: PATHS.allcourses, target: false },
    { text: 'Geeks Junior', link: PATHS.geeksJunior, target: false },
    { text: 'Geeks Pro', link: PATHS.careerCenter, target: false },
    { text: t('AboutUs'), link: PATHS.aboutUs, target: false },
    { text: t('Advantages'), link: PATHS.advantages, target: false },
    { text: 'GeekStudio', link: externalRecourse.geekstudio, target: true },
    { text: t('Vacancies'), link: PATHS.vacancies, target: false },
    { text: t('FAQ'), link: PATHS.FAQ, target: false },
    { text: t('SiteMap'), link: PATHS.mapSite, target: false },
    { text: t('PrivacyPolicy'), link: PATHS.privacy, target: false },
  ];

  useEffect(() => {
    dispatch(getFooter());
    dispatch(getSocialMedia());

    return () => null;
  }, [ dispatch ]);

  const { footer, footerStatus } = useSelector(detailFooter);
  const { socialMedia } = useSelector(detailSocialMedia);
  if (footerStatus !== REQUEST_STATUSES.SUCCEEDED) return null;
  return (
    <Box className={classes.footer} component='footer'>
      {
        footerStatus === REQUEST_STATUSES.REQUESTE
          ?
          <Loader />
          :
          <Container>
            <Box className={classes.logo}>
              <img src={logo} alt="#" />
            </Box>
            <Box className={classes.footerContent}>
              <Box className={classes.footerColumn}>
                <Typography className={classes.footerHeadText}>{t('Main')}</Typography>
                <ul className={classes.footerList}>
                  {linksFooter?.map((item, index) => {
                    return (
                      <li key={index}>
                        <Link
                          to={item.link}
                          target={item.target ? '_blank' : ''}
                          className={classes.footerLink}
                          rel="nofollow"
                        >
                          {item.text}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </Box>
              <Box className={classes.footerColumn}>
                <Typography className={classes.footerHeadText}>{t('Courses')}</Typography>
                <ul className={classes.footerList}>
                  {footer?.courses?.map((item, i) => {
                    return (
                      <li key={i}>
                        <Link className={classes.footerLink} to={`/course/${item.slug}`}>{item.title}</Link>
                      </li>
                    );
                  })}
                </ul>
              </Box>
              <Box className={classes.footerColumn}>
                <Typography className={classes.footerHeadText}>{t('Address')}</Typography>
                {footer?.locations?.map((item, i) => {
                  return (
                    <Box key={i} className={classes.footerList}>
                      <Typography className={classes.footerCity}>{item.city}</Typography>
                      <ul className={classes.footerList}>
                        {item.address.map((item, id) => <li key={id} className={classes.footerLiText}>{item}</li>)}
                      </ul>
                    </Box>
                  );
                })}
              </Box>
              <Box className={classes.footerColumn}>
                <Typography className={classes.footerHeadText}>{t('Contacts')}</Typography>
                <Box className={classes.footerColumn}>
                  <ul className={classes.footerList}>
                    {footer?.numbers?.slice(0, 3).map((item, i) => {
                      return (
                        <li key={i}>
                          <Link key={i} to={`tel:${item}`} className={classes.contacts}>{item}</Link>
                        </li>
                      );
                    })}
                  </ul>
                </Box>
                <Box className={classes.emails}>
                  <Box className={classes.email}>
                    <Typography className={classes.footerHeadText} >
                      {t('ForTraining')}
                    </Typography>
                    <Link to={`mailto:${emailsFooter.forTraining}`}
                      className={classes.emailLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {emailsFooter.forTraining}
                    </Link>
                  </Box>
                  <Box className={classes.email}>
                    <Typography className={classes.footerHeadText}>
                      {t('ForPartners')}
                    </Typography>
                    <Link to={`mailto:${emailsFooter.forPartners}`}
                      className={classes.emailLink}
                      target="_blank"
                      rel="noreferrer">
                      {emailsFooter.forPartners}
                    </Link>
                  </Box>
                  <Box className={classes.email}>
                    <Typography className={classes.footerHeadText}>
                      {t('ForInternship')}
                    </Typography>
                    <Link to={`mailto:${emailsFooter.forInternship}`}
                      className={classes.emailLink}
                      target="_blank"
                      rel="noreferrer">
                      {emailsFooter.forInternship}
                    </Link>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box className={classes.social}>
              {
                socialMedia?.map((item, i) =>
                  <a
                    key={i}
                    href={item.link}
                    target="_blank"
                    rel='noreferrer'
                    {...generateRel(true)}
                  >
                    <img
                      alt={item.active_icon_alt}
                      src={active === i ? item.active_icon : item.inactive_icon}
                      onMouseEnter={() => setActive(i)}
                      onMouseLeave={() => setActive(null)}
                    />
                  </a>)
              }
            </Box>
          </Container>
      }
      <Box className={classes.yellowFoot}>
        <a target={'_blank'} rel='noreferrer'
          href={externalRecourse.geekstudio} {...generateRel(true)}>Made by GeekStudio</a>
      </Box>
    </Box>
  );
};

export default Footer;
